import React from 'react';
import { Link, Route, Routes, useMatch } from 'react-router-dom';
import ComputePricing from './ComputePricing';
import PublicIPPricing from './PublicIPPricing';
import StoragePricing from './StoragePricing';
import ManagedDiskPricing from './ManagedDiskPricing';
import AppServicePricing from './AppServicePricing';
import AKSPricing from './AKSPricing';
import SQLPricing from './SQLPricing';
import CostEstimator from './CostEstimator';
import PriceComparison from './PriceComparison';
import SavedEstimates from './SavedEstimates';
import Breadcrumb from '../../components/ui/Breadcrumb';

const resourceTypes = [
  { value: 'compute', label: '🖥 Compute', group: 'Core' },
  { value: 'publicip', label: '🌐 Public IP', group: 'Core' },
  { value: 'storage', label: '💾 Storage', group: 'Core' },
  { value: 'manageddisk', label: '💿 Managed Disk', group: 'Core' },
  { value: 'appservice', label: '⚡ App Service', group: 'PaaS' },
  { value: 'aks', label: '☸️ AKS Nodes', group: 'PaaS' },
  { value: 'sql', label: '🗄 Azure SQL', group: 'PaaS' },
];

const toolTypes = [
  { value: 'estimator', label: '🧮 Cost Estimator', group: 'Tools' },
  { value: 'compare', label: '⚖️ Compare Regions', group: 'Tools' },
  { value: 'saved', label: '📂 Saved Estimates', group: 'Tools' },
];

const ROUTE_LABELS = {
  compute: 'Compute',
  publicip: 'Public IP',
  storage: 'Storage',
  manageddisk: 'Managed Disk',
  appservice: 'App Service',
  aks: 'AKS Nodes',
  sql: 'Azure SQL',
  estimator: 'Cost Estimator',
  compare: 'Compare Regions',
  saved: 'Saved Estimates',
};

const NavGroup = ({ title, items }) => (
  <div className="mb-3">
    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1 px-1">{title}</p>
    <div className="flex flex-wrap gap-2">
      {items.map((type) => (
        <Link
          key={type.value}
          to={`/pricing/${type.value}`}
          className="bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium py-1.5 px-3 rounded transition-colors"
        >
          {type.label}
        </Link>
      ))}
    </div>
  </div>
);

const PricingTool = () => {
  const subMatch = useMatch('/pricing/:sub');
  const currentSub = subMatch?.params?.sub;
  const currentLabel = currentSub ? ROUTE_LABELS[currentSub] : null;

  const breadcrumbs = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Pricing Tool', to: '/pricing' },
    ...(currentLabel ? [{ label: currentLabel }] : []),
  ];

  return (
    <div className="container mx-auto p-4">
      <Breadcrumb items={breadcrumbs} />

      <h1 className="text-3xl font-bold mb-1">Azure Pricing Tool</h1>
      <p className="text-gray-500 text-sm mb-6">
        Fetch live Azure Retail Prices. Data sourced from the{' '}
        <a
          href="https://learn.microsoft.com/en-us/rest/api/cost-management/retail-prices/azure-retail-prices"
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-600 hover:underline"
        >
          Azure Retail Prices API
        </a>.
      </p>

      <div className="mb-8 bg-gray-50 rounded-lg p-4 border">
        <NavGroup title="Core Resources" items={resourceTypes.filter((r) => r.group === 'Core')} />
        <NavGroup title="PaaS Services" items={resourceTypes.filter((r) => r.group === 'PaaS')} />
        <NavGroup title="Tools" items={toolTypes} />
      </div>

      <Routes>
        <Route path="compute" element={<ComputePricing />} />
        <Route path="publicip" element={<PublicIPPricing />} />
        <Route path="storage" element={<StoragePricing />} />
        <Route path="manageddisk" element={<ManagedDiskPricing />} />
        <Route path="appservice" element={<AppServicePricing />} />
        <Route path="aks" element={<AKSPricing />} />
        <Route path="sql" element={<SQLPricing />} />
        <Route path="estimator" element={<CostEstimator />} />
        <Route path="compare" element={<PriceComparison />} />
        <Route path="saved" element={<SavedEstimates />} />
      </Routes>
    </div>
  );
};

export default PricingTool;
